import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, KeyRound, LogOut, Eye, EyeOff, ShieldCheck, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { supabase } from "../lib/supabase";
import { Card, PageHeader, Button, Input, Label } from "../components/Primitives";

/** Read-only info row (label · value). */
function InfoRow({ icon: Icon, label, value, mono = false, testid }) {
  return (
    <div className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-carbon-800 border border-carbon-600 flex items-center justify-center text-ink-500 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500">{label}</div>
        <div className={`text-sm text-ink-900 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
      </div>
    </div>
  );
}

export default function Security() {
  const { t } = useI18n();
  const { user, resetPassword, signOut } = useAuth();
  const navigate = useNavigate();

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!cur || !nw || !confirm) { toast.error(t("security.validation.required")); return; }
    if (nw.length < 8) { toast.error(t("security.validation.tooShort")); return; }
    if (nw !== confirm) { toast.error(t("security.validation.mismatch")); return; }
    if (nw === cur) { toast.error(t("security.validation.sameAsCurrent")); return; }
    if (!user?.email) { toast.error(t("security.validation.noEmail")); return; }
    setSaving(true);
    try {
      // 1) Re-verify identity with the current password (Supabase does not require
      //    the old password on updateUser, so we assert it explicitly for safety).
      const { error: reauthErr } = await supabase.auth.signInWithPassword({
        email: user.email, password: cur,
      });
      if (reauthErr) {
        toast.error(t("security.validation.wrongCurrent"));
        setSaving(false);
        return;
      }
      // 2) Update the password via the authenticated Supabase Auth flow.
      const { error: updErr } = await supabase.auth.updateUser({ password: nw });
      if (updErr) {
        toast.error(updErr.message || t("security.passwordUpdateFailed"));
        setSaving(false);
        return;
      }
      toast.success(t("security.passwordUpdated"));
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      toast.error(err?.message || t("security.passwordUpdateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    if (!user?.email || resetBusy) return;
    setResetBusy(true);
    try {
      const { error } = await resetPassword(user.email);
      if (error) toast.error(error.message);
      else toast.success(t("security.resetSent"));
    } finally { setResetBusy(false); }
  };

  const doSignOut = async () => {
    try { await signOut(); navigate("/", { replace: true }); }
    catch (e) { toast.error(e?.message || "Sign out failed"); }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 animate-fade-up" data-testid="security-page">
      <PageHeader title={t("security.title")} sub={t("security.subtitle")} />

      {/* Current identity */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
          {t("security.identityTitle")}
        </div>
        <Card data-testid="security-identity">
          <InfoRow icon={Mail} label={t("security.identityEmail")} value={user?.email} mono testid="security-email" />
        </Card>
      </section>

      {/* Change password */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
          <KeyRound className="w-3 h-3" /> {t("security.changePasswordTitle")}
        </div>
        <Card className="p-4">
          <form onSubmit={changePassword} className="space-y-3" data-testid="security-change-form">
            <div>
              <Label>{t("security.currentPassword")}</Label>
              <div className="relative">
                <Input
                  type={showCur ? "text" : "password"}
                  value={cur}
                  onChange={(e) => setCur(e.target.value)}
                  autoComplete="current-password"
                  data-testid="security-current-input"
                />
                <button type="button" onClick={() => setShowCur((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900" data-testid="security-current-toggle">
                  {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{t("security.newPassword")}</Label>
              <div className="relative">
                <Input
                  type={showNew ? "text" : "password"}
                  value={nw}
                  onChange={(e) => setNw(e.target.value)}
                  autoComplete="new-password"
                  data-testid="security-new-input"
                />
                <button type="button" onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900" data-testid="security-new-toggle">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[11px] text-ink-500 mt-1">{t("security.passwordHint")}</div>
            </div>
            <div>
              <Label>{t("security.confirmPassword")}</Label>
              <Input
                type={showNew ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="security-confirm-input"
              />
            </div>
            <Button type="submit" disabled={saving} data-testid="security-change-submit" className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {saving ? t("security.updating") : t("security.updatePassword")}
            </Button>
          </form>
        </Card>
      </section>

      {/* Password recovery — reuses the existing reset flow */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
          <RotateCcw className="w-3 h-3" /> {t("security.recoveryTitle")}
        </div>
        <Card className="p-4" data-testid="security-recovery">
          <p className="text-[12px] text-ink-700 mb-3 leading-relaxed">{t("security.recoveryHint")}</p>
          <Button onClick={sendReset} disabled={resetBusy} data-testid="security-send-reset"
            className="!bg-carbon-800 !border-carbon-600 !text-ink-900 hover:!border-electric-500/40">
            {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {t("security.sendResetEmail")}
          </Button>
        </Card>
      </section>

      {/* Sessions — sign out */}
      <section className="mb-16">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" /> {t("security.sessionsTitle")}
        </div>
        <Card className="p-4" data-testid="security-sessions">
          <p className="text-[12px] text-ink-700 mb-3 leading-relaxed">{t("security.sessionsHint")}</p>
          <button onClick={doSignOut} data-testid="security-signout"
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-carbon-600 text-ink-700 hover:text-ink-900 hover:border-electric-500/40 transition">
            <LogOut className="w-4 h-4" />
            {t("security.signOut")}
          </button>
        </Card>
      </section>
    </div>
  );
}
